// ════════════════════════════════════════════════════════════════════════════════════════════════
// FORMATOS — cómo se pinta un dato en la pantalla de CADA clínica (fechas, horas, importes, números)
//
// Biango se vende en toda España y aspira a más. Hasta hoy la app escribía a mano `'es-ES'` en 186
// sitios, `Europe/Madrid` en 16 y el euro en 167: eso no es un valor por defecto, es una decisión
// clavada. Una clínica de Las Palmas va una hora por detrás de la península y la app le estaba
// mintiendo. Aquí vive la traducción de "un dato" a "un texto", y la CONFIGURACIÓN de esa clínica
// entra por parámetro. Nada de esto sabe nada de React ni de la red: son funciones puras, para que
// se puedan probar con `node --test` (backend/services/formatos.test.js).
//
// ════════════════════════════════════════════════════════════════════════════════════════════════
// LO MÁS IMPORTANTE DE TODO EL FICHERO — UNA FECHA DE NEGOCIO NO PASA POR new Date()
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// Es el fallo más repetido de este repositorio y ya ha costado datos (ver la memoria del proyecto
// "feedback-fecha-toISOString-shift" y la regla 3 de CLAUDE.md). Hay DOS cosas distintas que la
// gente llama "fecha", y confundirlas corre el calendario un día:
//
//   1) UN DÍA DEL CALENDARIO (una "fecha de negocio"): el día de una cita, una fecha de nacimiento,
//      la fecha de una factura. En la base es un DATE y aquí llega como TEXTO 'YYYY-MM-DD'.
//      NO ES UN INSTANTE: no tiene hora, y por tanto NO TIENE ZONA HORARIA. El 31 de marzo es el
//      31 de marzo en Las Palmas, en Madrid y en Tokio.
//
//      `new Date('2026-03-31')` lo interpreta como MEDIANOCHE UTC. Al pintarlo en un huso negativo
//      (o al volver a serializarlo con toISOString) sale el DÍA ANTERIOR:
//
//          TZ=America/New_York  ->  new Date('2026-03-31').getDate() === 30      // comprobado
//
//      Por eso este fichero trata 'YYYY-MM-DD' COMO TEXTO: parte la cadena y coloca los trozos en
//      el orden que pida la clínica. No se construye ningún Date. No hay Date que corregir.
//
//   2) UN INSTANTE REAL: cuándo se registró una factura, cuándo entró un mensaje de WhatsApp,
//      "hace 5 minutos". En la base es un TIMESTAMPTZ y aquí llega como ISO con `Z` o con desfase.
//      Eso SÍ es un Date, y se pinta EN LA ZONA DE LA CLÍNICA (no en la del ordenador que mira:
//      un gestor conectado desde Madrid a una clínica canaria tiene que ver la hora de la clínica).
//      Se hace con Intl.DateTimeFormat({ timeZone }), que ignora la zona del proceso por completo.
//
//      El caso real que motivó toda esta tanda: una factura emitida a las 23:40 del 31 de marzo en
//      Las Palmas es el instante 2026-03-31T22:40:00Z. Leída en Europe/Madrid sale 01/04/2026 —
//      otro trimestre, otro modelo 111, la declaración descuadrada. Leída en Atlantic/Canary sale
//      31/03/2026. La zona no es cosmética: cambia el número que se declara a Hacienda.
//
//   3) Y UN TERCERO que hay que nombrar: 'YYYY-MM-DD HH:MM:SS' SIN Z ni desfase (un TIMESTAMP
//      WITHOUT TIME ZONE servido tal cual). Eso es un RELOJ DE PARED: dice qué marcaba el reloj,
//      no en qué instante del universo. Se lee TEXTUAL, sin convertir nada, porque convertirlo
//      sería inventarse una zona que el dato no trae.
//
// LO QUE ESTE FICHERO NO PUEDE ARREGLAR, y conviene saberlo antes de culparle:
// si el backend devuelve una columna DATE **cruda** (sin `to_char`), el driver de pg la convierte a
// un Date a medianoche LOCAL DEL SERVIDOR y `res.json()` la serializa a UTC: '2026-03-31' sale por
// el cable como '2026-03-30T22:00:00.000Z'. Cuando eso llega aquí ya es indistinguible de un
// instante real, y ninguna heurística lo puede deshacer sin romper los instantes de verdad. El
// arreglo está en el SELECT: `to_char(col,'YYYY-MM-DD') AS col` (regla 3 de CLAUDE.md). Mientras se
// arregla el endpoint, la pantalla tiene la salida de emergencia `{ tipo: 'dia' }`, que fuerza la
// lectura de los diez primeros caracteres como día del calendario.
// ════════════════════════════════════════════════════════════════════════════════════════════════

// ── LA CONFIGURACIÓN ────────────────────────────────────────────────────────────────────────────
// Los mismos nombres que las columnas de la tabla `clinicas` (migración 2026-11-28), para que lo
// que devuelve GET /api/regional se pueda pasar tal cual sin traducir claves por el camino.
//
// Y OJO CON ESTE DEFECTO: 'Europe/Madrid' es el sitio donde está el 95 % del mercado, así que es un
// defecto sensato — pero es un DEFECTO, no una decisión. Existe para que una instalación recién
// creada no pinte en blanco mientras carga, no para ahorrarle a nadie elegir. Una clínica canaria
// tiene que poder cambiarlo antes de facturar nada (por eso va en Primeros pasos).
export const REGIONAL_POR_DEFECTO = Object.freeze({
  zona_horaria: 'Europe/Madrid',
  idioma: 'es',
  formato_fecha: 'DD/MM/AAAA',
  formato_hora: '24h',
  moneda: 'EUR',
  separador_decimal: ',',
  primer_dia_semana: 1,
})

// El hueco. Es el mismo guion largo que ya usa el resto de la app para "aquí no hay dato", y es
// deliberado que TODAS las funciones de aquí devuelvan esto y nunca 'Invalid Date', 'NaN',
// 'undefined' ni una cadena vacía: una celda vacía se lee como un fallo de carga; un guion se lee
// como "no hay nada que poner", que es lo que pasa de verdad.
export const VACIO = '—'

// Formatos de fecha que la pantalla de Ajustes ofrece. La lista es abierta a propósito: el pintado
// funciona por plantilla de tokens (abajo), así que añadir 'DD-MM-AAAA' o 'AAAA/MM/DD' es meterlo
// aquí y no tocar nada más.
export const FORMATOS_FECHA = Object.freeze(['DD/MM/AAAA', 'AAAA-MM-DD', 'MM/DD/AAAA'])
export const FORMATOS_HORA = Object.freeze(['24h', '12h'])

// Para el "así se verá" en vivo de la pantalla de Ajustes: que el ejemplo sea SIEMPRE el mismo día
// y el mismo importe en todas las filas, y que sea un día que delate el orden (31 no puede ser mes).
export const EJEMPLO_FECHA = '2026-03-31'
export const EJEMPLO_INSTANTE = '2026-03-31T22:40:00Z'
export const EJEMPLO_IMPORTE = '1234.56'

// ── MONEDAS: SÍMBOLO Y DECIMALES ────────────────────────────────────────────────────────────────
// Esto es solo para PINTAR. La lista de monedas que se pueden ELEGIR la sirve el servidor
// (GET /api/regional/opciones), y por eso una moneda que no esté en esta tabla no puede romper la
// pantalla: se pinta con su código de tres letras detrás del importe ("1.234,56 PLN"), que es feo
// pero es correcto y legible. Nunca un importe sin unidad.
const MONEDA_DEFECTO = { decimales: 2 }
export const MONEDAS = Object.freeze({
  EUR: { simbolo: '€',   decimales: 2 },
  USD: { simbolo: '$',   decimales: 2 },
  GBP: { simbolo: '£',   decimales: 2 },
  CHF: { simbolo: 'CHF', decimales: 2 },
  MAD: { simbolo: 'MAD', decimales: 2 },
  MXN: { simbolo: '$',   decimales: 2 },
  ARS: { simbolo: '$',   decimales: 2 },
  COP: { simbolo: '$',   decimales: 2 },
  CLP: { simbolo: '$',   decimales: 0 },
  BRL: { simbolo: 'R$',  decimales: 2 },
  // Sin decimales de verdad: pintar '¥1.234,00' es escribir una precisión que la moneda no tiene.
  JPY: { simbolo: '¥',   decimales: 0 },
})

// ── DÓNDE VA EL SÍMBOLO: LO MANDA EL IDIOMA, NO LA MONEDA ───────────────────────────────────────
// Este fue el primer diseño equivocado de este fichero, y lo cazó el test: se había puesto la
// posición dentro de cada moneda ("el dólar va delante"), y entonces una clínica española que cobre
// en dólares escribía '$1.234,56'. En español no se escribe así — se escribe '1.234,56 $', igual
// que el euro. La posición es una convención del IDIOMA, no de la moneda: el inglés abre el importe
// con el símbolo sea cual sea la moneda (€1,234.56) y el español lo cierra siempre.
//
// Comprobado contra lo que hace el propio navegador:
//   (1234.56).toLocaleString('es-ES', {style:'currency', currency:'USD'})  ->  '1.234,56 US$'
//   (1234.56).toLocaleString('en-US', {style:'currency', currency:'EUR'})  ->  '€1,234.56'
//
// El defecto para un idioma que no esté en la lista es 'despues', que es lo europeo y lo que le
// toca al mercado de este producto.
const POSICION_MONEDA = Object.freeze({
  es: 'despues', ca: 'despues', gl: 'despues', eu: 'despues',
  fr: 'despues', pt: 'despues', de: 'despues', it: 'despues', nl: 'despues',
  en: 'antes',
})

/** Dónde se coloca el símbolo en ese idioma. Se acepta 'es-ES' además de 'es'. */
export function posicionMoneda(idioma) {
  const base = String(idioma || '').trim().toLowerCase().split(/[-_]/)[0]
  return POSICION_MONEDA[base] || 'despues'
}

/** La ficha de pintado de una moneda. Una desconocida se pinta con su propio código. */
export function monedaInfo(codigo) {
  const c = String(codigo || '').trim().toUpperCase()
  const m = MONEDAS[c]
  if (m) return { codigo: c, ...m }
  return { codigo: c || 'EUR', simbolo: c || '€', ...MONEDA_DEFECTO }
}

// ── NORMALIZAR LA CONFIGURACIÓN ─────────────────────────────────────────────────────────────────
// Todo lo de aquí abajo tiene que aguantar que le pasen `null`, `undefined`, un objeto a medias o el
// objeto entero. Motivo práctico: estas funciones las van a llamar cientos de pantallas, algunas
// mientras la configuración todavía está cargando, y ninguna puede reventar por eso — como mucho
// pinta con los valores por defecto una décima de segundo.
//
// Se aceptan también las claves en camelCase porque la configuración cruza una API y un contexto de
// React antes de llegar aquí, y basta con que alguien la mapee por el camino para que un
// `zonaHoraria` silencioso deje a una clínica pintando en la zona de otra. Cuesta una línea.
export function normalizar(regional) {
  const r = regional || {}
  const uno = (...claves) => {
    for (const k of claves) {
      const v = r[k]
      if (v !== undefined && v !== null && v !== '') return v
    }
    return undefined
  }
  const cfg = {
    zona_horaria: uno('zona_horaria', 'zonaHoraria', 'timeZone') || REGIONAL_POR_DEFECTO.zona_horaria,
    idioma: uno('idioma', 'lang', 'locale') || REGIONAL_POR_DEFECTO.idioma,
    formato_fecha: uno('formato_fecha', 'formatoFecha') || REGIONAL_POR_DEFECTO.formato_fecha,
    formato_hora: uno('formato_hora', 'formatoHora') || REGIONAL_POR_DEFECTO.formato_hora,
    moneda: uno('moneda', 'currency') || REGIONAL_POR_DEFECTO.moneda,
    separador_decimal: uno('separador_decimal', 'separadorDecimal') || REGIONAL_POR_DEFECTO.separador_decimal,
    primer_dia_semana: Number(uno('primer_dia_semana', 'primerDiaSemana') ?? REGIONAL_POR_DEFECTO.primer_dia_semana),
  }
  // El separador de MILES se deduce del decimal, que es lo único que la clínica elige. Son las dos
  // convenciones que existen y mezclarlas ('1.234.56') no lo entiende nadie. Se permite además
  // fijarlo a mano por si algún día hace falta el espacio fino (Francia escribe '1 234,56').
  const miles = uno('separador_miles', 'separadorMiles')
  cfg.separador_miles = miles !== undefined ? miles : (cfg.separador_decimal === ',' ? '.' : ',')
  return cfg
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// LECTURA DEL VALOR — aquí es donde se decide "día del calendario" o "instante", y es TODO el asunto
// ════════════════════════════════════════════════════════════════════════════════════════════════

// 'YYYY-MM-DD' exacto: un día del calendario. Es lo que devuelve `to_char(col,'YYYY-MM-DD')`.
const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})$/
// Tolerante con el relleno de ceros ('2026-3-1'). Si no estuviera, esa cadena caería en `new Date`,
// que la parsearía como hora LOCAL DEL NAVEGADOR y volvería a atar el día a la zona del que mira.
const RE_DIA_LAXA = /^(\d{4})-(\d{1,2})-(\d{1,2})$/
// Reloj de pared: fecha y hora SIN zona. Que no traiga zona se comprueba aparte, antes de llegar
// aquí, para no aceptar por este camino lo que sí la trae.
const RE_PARED = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/
// Una hora suelta: columnas TIME ('09:45:00', '9:45').
const RE_SOLO_HORA = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
// Marca de zona al final de la cadena: 'Z', '+02:00', '-0500'.
const RE_TRAE_ZONA = /(?:Z|[+-]\d{2}:?\d{2})$/i
// UN INSTANTE DE VERDAD, y solo eso: fecha, hora y zona. Lo que sirve un TIMESTAMPTZ por JSON.
//
// ES A PROPÓSITO QUE SEA ESTRICTA, y es el segundo agujero que cazó el test. `new Date(texto)` no
// se limita al ISO-8601: cuando no reconoce el formato se pone a adivinar, y adivina MAL sin avisar.
// Con un campo histórico escrito a mano en una ficha:
//
//     new Date('verano de 2019')  ->  2018-12-31T23:00:00.000Z      // comprobado
//
// Es decir: la pantalla habría enseñado "31/12/2018" como si fuera un dato bueno, y nadie lo
// distingue de una fecha de verdad. Un dato inventado con aspecto de dato es peor que no tener dato.
// Por eso, en modo automático solo pasa por `new Date` lo que TIENE forma de instante. Lo demás se
// devuelve como el texto que es.
const RE_ISO_INSTANTE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/i

const dosDig = (v) => String(v).padStart(2, '0')

/**
 * Convierte un INSTANTE (Date) a los números de calendario y reloj EN LA ZONA DE LA CLÍNICA.
 *
 * Se usa Intl.DateTimeFormat con `timeZone` explícito y se leen las PARTES una a una en vez de
 * confiar en el texto que devuelve: así el resultado no depende ni de la zona del proceso ni de los
 * datos de idioma del navegador — solo salen números, y los ordenamos nosotros. `toLocaleDateString`
 * haría justo lo contrario (formato distinto según el navegador) y por eso no aparece en este fichero.
 *
 * `hourCycle: 'h23'` y no `hour12: false`: con `hour12: false` hay motores que devuelven "24" para
 * la medianoche, y "24:00" en una tabla de horas es un error visible.
 */
function _enZona(fecha, zona) {
  const opciones = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }
  let partes
  try {
    partes = new Intl.DateTimeFormat('en-US', { ...opciones, timeZone: zona }).formatToParts(fecha)
  } catch {
    // Una zona que este navegador no conoce reventaría aquí y se llevaría por delante la pantalla
    // entera. El servidor la valida al guardarla (pieza B), pero una versión vieja de un móvil puede
    // no traer una zona nueva: antes de romper, se pinta en UTC. Es un dato peor, no una pantalla
    // en blanco.
    partes = new Intl.DateTimeFormat('en-US', { ...opciones, timeZone: 'UTC' }).formatToParts(fecha)
  }
  const o = {}
  for (const p of partes) if (p.type !== 'literal') o[p.type] = p.value
  return {
    y: String(o.year).padStart(4, '0'), m: o.month, d: o.day,
    hh: o.hour, mm: o.minute, ss: o.second,
    conFecha: true, conHora: true,
  }
}

/**
 * EL REPARTIDOR. Devuelve los trozos ya listos para pintar, o null si no hay nada que pintar.
 *
 * `tipo`:
 *   'auto'     (por defecto) — decide por la FORMA del valor, como explica la cabecera.
 *   'dia'      — fuerza "día del calendario": de una cadena se leen los diez primeros caracteres y
 *                no se convierte nada. Es la salida de emergencia para endpoints que todavía sirven
 *                DATE crudos; lo correcto sigue siendo arreglar el SELECT con to_char.
 *   'instante' — fuerza "instante real": se construye el Date y se lee en la zona de la clínica.
 */
function _partes(valor, cfg, tipo = 'auto') {
  if (valor === null || valor === undefined) return null

  // ── Un Date ya es un instante por definición: no tiene día del calendario propio, lo tiene EN una
  // zona. Se lee en la de la clínica.
  if (valor instanceof Date) {
    if (Number.isNaN(valor.getTime())) return null
    const p = _enZona(valor, cfg.zona_horaria)
    return tipo === 'dia' ? { ...p, conHora: false } : p
  }

  // ── Un número es epoch en milisegundos (Date.now(), etc.).
  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) return null
    const p = _enZona(new Date(valor), cfg.zona_horaria)
    return tipo === 'dia' ? { ...p, conHora: false } : p
  }

  if (typeof valor !== 'string') return null
  const s = valor.trim()
  if (s === '') return null

  // ── FORZADO A DÍA: los diez primeros caracteres, TEXTUALES. Sin Date por ninguna parte.
  if (tipo === 'dia') {
    const m = RE_DIA_LAXA.exec(s.slice(0, 10)) || RE_DIA_LAXA.exec(s)
    if (m) return { y: m[1], m: dosDig(m[2]), d: dosDig(m[3]), conFecha: true, conHora: false }
    return RE_ISO_INSTANTE.test(s) ? _instanteDeTexto(s, cfg, true) : null
  }

  // ── FORZADO A INSTANTE: aquí sí vale cualquier cosa que `new Date` sepa leer, porque quien lo
  // pide está afirmando que lo es. En automático no, por lo que explica RE_ISO_INSTANTE.
  if (tipo === 'instante') return _instanteDeTexto(s, cfg, false)

  // ── EL CAMINO QUE SALVA EL DÍA. 'YYYY-MM-DD' se parte y ya está.
  const dia = RE_DIA.exec(s) || RE_DIA_LAXA.exec(s)
  if (dia) return { y: dia[1], m: dosDig(dia[2]), d: dosDig(dia[3]), conFecha: true, conHora: false }

  // ── Reloj de pared: fecha y hora sin zona. Solo si NO trae zona detrás; si la trae es un
  // instante de verdad y va por el otro camino.
  if (!RE_TRAE_ZONA.test(s)) {
    const p = RE_PARED.exec(s)
    if (p) {
      return {
        y: p[1], m: dosDig(p[2]), d: dosDig(p[3]),
        hh: dosDig(p[4]), mm: p[5], ss: p[6] || '00',
        conFecha: true, conHora: true,
      }
    }
    // ── Una hora suelta (columna TIME). Tampoco tiene zona.
    const h = RE_SOLO_HORA.exec(s)
    if (h) return { hh: dosDig(h[1]), mm: h[2], ss: h[3] || '00', conFecha: false, conHora: true }
  }

  // Solo lo que tiene forma de instante llega a `new Date`. Lo demás no es una fecha: es texto.
  return RE_ISO_INSTANTE.test(s) ? _instanteDeTexto(s, cfg, false) : null
}

function _instanteDeTexto(s, cfg, sinHora) {
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  const p = _enZona(d, cfg.zona_horaria)
  return sinHora ? { ...p, conHora: false } : p
}

// ── LOS DOS HUECOS, QUE NO SON EL MISMO ─────────────────────────────────────────────────────────
// Hay que distinguir "no hay dato" de "no he sabido leer el dato", porque se pintan distinto.

// NO HAY DATO (o el dato no tiene esa pieza: un día del calendario no tiene hora). Guion y ya.
const _hueco = (opciones) => (opciones && opciones.vacio !== undefined ? opciones.vacio : VACIO)

// NO HE SABIDO LEERLO. Con null, cadena vacía, NaN o un Date inválido: el guion, sin discusión.
//
// Con una CADENA que no hemos sabido interpretar se devuelve la cadena tal cual, y es deliberado:
// en esta base hay campos históricos con fechas escritas a mano ("verano de 2019", "pendiente"), y
// esconderlas detrás de un guion es borrar información delante del usuario sin decírselo. Lo que no
// se devuelve JAMÁS es 'Invalid Date', ni una fecha inventada a partir de ese texto (ver
// RE_ISO_INSTANTE). En `dinero` y `numero` la regla es la contraria, y también a propósito: ver allí.
const _huecoFecha = (valor, opciones) => {
  if (opciones && opciones.vacio !== undefined) return opciones.vacio
  if (typeof valor === 'string') {
    const s = valor.trim()
    if (s !== '') return s
  }
  return VACIO
}

// ── PINTADO ─────────────────────────────────────────────────────────────────────────────────────

// Plantilla de tokens: 'DD/MM/AAAA' -> '31/03/2026'. Se sustituyen los tokens y TODO lo demás queda
// literal, así que el separador lo pone la plantilla y no hay que tocar código para admitir formatos
// nuevos. Se aceptan los tokens en castellano (AAAA) y en inglés (YYYY) porque los dos se escriben
// solos: el desplegable ofrece los de la casa y el resto entra por la puerta de atrás sin romper.
const RE_TOKENS = /AAAA|YYYY|AA|YY|MM|DD/g
function _pintarFecha(p, plantilla) {
  const patron = String(plantilla || REGIONAL_POR_DEFECTO.formato_fecha)
  return patron.replace(RE_TOKENS, (tok) => {
    switch (tok) {
      case 'AAAA': case 'YYYY': return p.y
      case 'AA': case 'YY': return String(p.y).slice(-2)
      case 'MM': return p.m
      case 'DD': return p.d
      default: return tok
    }
  })
}

// 24h -> '14:30'. 12h -> '2:30 PM'.
//
// El 12 horas se compone A MANO y no con Intl. Motivo: Intl en español devuelve '2:30 p. m.' con
// espacios finos que no se ven, cambian entre versiones de navegador y se rompen al copiar y pegar
// o al exportar a CSV. Aquí el texto que se ve es exactamente el que se copia. La medianoche es
// '12:15 AM' y el mediodía '12:00 PM', que es la convención y además el sitio donde todo el mundo
// se equivoca al programarlo (el 0 no pasa a 12 por accidente: hay que escribirlo).
function _pintarHora(p, formato, conSegundos) {
  const seg = conSegundos ? ':' + (p.ss || '00') : ''
  if (String(formato) === '12h') {
    const h24 = Number(p.hh)
    const sufijo = h24 < 12 ? 'AM' : 'PM'
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12
    return `${h12}:${p.mm}${seg} ${sufijo}`
  }
  return `${p.hh}:${p.mm}${seg}`
}

// ── LAS CINCO FUNCIONES DEL CONTRATO ────────────────────────────────────────────────────────────

/**
 * Un día del calendario, o la parte de fecha de un instante, en el formato de la clínica.
 * Opciones: { formato, tipo: 'auto'|'dia'|'instante', vacio }
 */
export function fecha(valor, regional, opciones = {}) {
  const cfg = normalizar(regional)
  const p = _partes(valor, cfg, opciones.tipo)
  if (!p) return _huecoFecha(valor, opciones)
  if (!p.conFecha) return _hueco(opciones)
  return _pintarFecha(p, opciones.formato || cfg.formato_fecha)
}

/**
 * La hora, en 24h o 12h según la clínica. Un día del calendario NO tiene hora: devuelve el hueco,
 * porque inventarse un '00:00' es afirmar algo que el dato no dice.
 * Opciones: { formato, segundos, tipo, vacio }
 */
export function hora(valor, regional, opciones = {}) {
  const cfg = normalizar(regional)
  const p = _partes(valor, cfg, opciones.tipo)
  if (!p) return _huecoFecha(valor, opciones)
  // Un día del calendario no tiene hora, y NO se devuelve la fecha entera aquí: quien pide la hora
  // espera una hora, y pintar '2026-03-31' en la columna "Hora" es peor que dejarla vacía.
  if (!p.conHora) return _hueco(opciones)
  return _pintarHora(p, opciones.formato || cfg.formato_hora, opciones.segundos)
}

/**
 * Fecha y hora juntas. Si el valor solo trae fecha se pinta solo la fecha (y no 'fecha —'); si solo
 * trae hora, solo la hora.
 * Opciones: { formato, formatoHora, separador, segundos, tipo, vacio }
 */
export function fechaHora(valor, regional, opciones = {}) {
  const cfg = normalizar(regional)
  const p = _partes(valor, cfg, opciones.tipo)
  if (!p) return _huecoFecha(valor, opciones)
  const sep = opciones.separador !== undefined ? opciones.separador : ' '
  const f = p.conFecha ? _pintarFecha(p, opciones.formato || cfg.formato_fecha) : ''
  const h = p.conHora ? _pintarHora(p, opciones.formatoHora || cfg.formato_hora, opciones.segundos) : ''
  if (f && h) return f + sep + h
  return f || h || _hueco(opciones)
}

/**
 * Un importe con su símbolo, en su sitio y con los separadores de la clínica.
 * Opciones: { moneda, decimales, simbolo: false, vacio }
 *
 * OJO CON EL CERO: 0 es un importe, no un hueco. `if (!valor) return '—'` es el bug clásico de esta
 * función y deja las deudas saldadas pintadas como si no se supiera cuánto se debe.
 */
export function dinero(valor, regional, opciones = {}) {
  const cfg = normalizar(regional)
  const mon = monedaInfo(opciones.moneda || cfg.moneda)
  const dec = opciones.decimales !== undefined ? opciones.decimales : mon.decimales
  const txt = _texto(valor, cfg, { decimales: dec, minimos: dec })
  // En dinero, lo que no es un número NO se pinta tal cual (al revés que en las fechas): una celda
  // de importes con la palabra "pendiente" dentro se lee como una cantidad y nadie la distingue de
  // un dato bueno. Aquí un valor ilegible es corrupción, no una nota histórica.
  if (txt === null) return opciones.vacio !== undefined ? opciones.vacio : VACIO
  if (opciones.simbolo === false) return txt

  // El signo va SIEMPRE delante del todo, también cuando el símbolo abre: '-$1,234.56'. Escribirlo
  // como '$-1,234.56' no es correcto en ninguna convención.
  const negativo = txt.startsWith('-')
  const cuerpo = negativo ? txt.slice(1) : txt
  const signo = negativo ? '-' : ''

  // La posición la manda el IDIOMA (ver POSICION_MONEDA arriba). Se puede forzar por llamada para
  // el caso raro que siempre aparece: una tabla comparativa, un documento de otro país.
  const antes = (opciones.posicion || posicionMoneda(cfg.idioma)) === 'antes'
  // Espacio normal, no espacio duro: el texto que se ve es el mismo que se copia y el que sale en
  // un CSV. Que '1.234,56' y '€' no se separen al final de una línea es cosa del CSS (nowrap), no
  // de meter un carácter invisible dentro del dato.
  return antes ? `${signo}${mon.simbolo}${cuerpo}` : `${signo}${cuerpo} ${mon.simbolo}`
}

/**
 * Un número con los separadores de la clínica.
 * Opciones: { decimales, maxDecimales, agrupar: false, vacio }
 *
 * Sin `decimales`, los decimales son los que traiga el dato hasta un máximo de 2, sin ceros de
 * relleno: 12 -> '12', 12.5 -> '12,5', 12.345 -> '12,35'. Con `decimales` se fijan exactamente.
 */
export function numero(valor, regional, opciones = {}) {
  const cfg = normalizar(regional)
  const fijos = opciones.decimales
  const txt = _texto(valor, cfg, {
    decimales: fijos !== undefined ? fijos : (opciones.maxDecimales !== undefined ? opciones.maxDecimales : 2),
    minimos: fijos !== undefined ? fijos : 0,
    agrupar: opciones.agrupar,
  })
  if (txt === null) return opciones.vacio !== undefined ? opciones.vacio : VACIO
  return txt
}

/** 'YYYY-MM-DD' de HOY en la zona de la clínica. El sustituto de `new Date().toISOString().slice(0,10)`
 *  y de los `toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })` que están clavados por la app. */
export function hoy(regional, ahoraMismo) {
  const cfg = normalizar(regional)
  const p = _enZona(ahoraMismo instanceof Date ? ahoraMismo : new Date(), cfg.zona_horaria)
  return `${p.y}-${p.m}-${p.d}`
}

/** 'YYYY-MM-DDTHH:MM' de AHORA en la zona de la clínica (para rellenar un <input type="datetime-local">). */
export function ahora(regional, ahoraMismo) {
  const cfg = normalizar(regional)
  const p = _enZona(ahoraMismo instanceof Date ? ahoraMismo : new Date(), cfg.zona_horaria)
  return `${p.y}-${p.m}-${p.d}T${p.hh}:${p.mm}`
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// NÚMEROS — el redondeo se hace SOBRE EL TEXTO, no sobre un float
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// Postgres sirve NUMERIC como CADENA justamente para que no pierda precisión por el camino, y el
// primer impulso —`Number(x).toFixed(2)`— la tira a la basura en la puerta: 1234.565 no existe en
// coma flotante (el double más cercano es 1234.5649999...), así que toFixed devuelve '1234.56' y el
// importe sale un céntimo por debajo. En una factura eso es un descuadre.
//
// Por eso, cuando el valor llega como cadena decimal exacta, se redondea MOVIENDO DÍGITOS: se corta
// la parte que sobra, se mira el primer dígito descartado y se suma uno con acarreo si es 5 o más
// (redondeo comercial al alza, el de toda la vida). No hay float en ningún paso.

const RE_DECIMAL = /^[+-]?\d+(?:\.\d+)?$/

/** Pasa cualquier entrada a una cadena decimal exacta, o null si no es un número. */
function _aDecimal(valor) {
  if (valor === null || valor === undefined) return null
  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) return null
    const s = String(valor)
    // Notación exponencial ('1e-7', '1e+22'): se deshace con toFixed, que aquí sí vale porque no hay
    // una cadena exacta que preservar — el float ya es el dato.
    return /e/i.test(s) ? valor.toFixed(20).replace(/0+$/, '').replace(/\.$/, '') : s
  }
  if (typeof valor === 'string') {
    const s = valor.trim()
    if (s === '' || !RE_DECIMAL.test(s)) return null
    return s
  }
  return null
}

/** Suma 1 a una cadena de dígitos, con acarreo. '99' -> '100'. */
function _sumarUno(digitos) {
  const d = digitos.split('')
  let i = d.length - 1
  while (i >= 0) {
    if (d[i] === '9') { d[i] = '0'; i-- } else { d[i] = String(Number(d[i]) + 1); return d.join('') }
  }
  return '1' + d.join('')
}

/**
 * Redondeo comercial (mitad hacia arriba) sobre la cadena. Devuelve { neg, ent, fra }.
 * `decimales` es el MÁXIMO; `minimos`, cuántos se conservan aunque sean ceros.
 */
function _redondear(decimal, decimales, minimos) {
  const neg = decimal.startsWith('-')
  const sinSigno = decimal.replace(/^[+-]/, '')
  const [entRaw, fraRaw = ''] = sinSigno.split('.')
  const max = Math.max(0, Math.min(20, Math.trunc(Number(decimales) || 0)))

  let ent = entRaw.replace(/^0+(?=\d)/, '')
  let fra = fraRaw

  if (fra.length > max) {
    const descartado = fra.charCodeAt(max) - 48
    let digitos = ent + fra.slice(0, max)
    if (descartado >= 5) digitos = _sumarUno(digitos)
    fra = max > 0 ? digitos.slice(digitos.length - max) : ''
    ent = digitos.slice(0, digitos.length - max) || '0'
  }

  // Los ceros que sobran por la derecha se quitan, pero nunca por debajo del mínimo: un importe pide
  // 2 y '1.234,5 €' está mal escrito; una cantidad no pide ninguno y '12,00' es ruido.
  const min = Math.max(0, Math.min(max, Math.trunc(Number(minimos) || 0)))
  while (fra.length > min && fra.endsWith('0')) fra = fra.slice(0, -1)
  fra = fra.padEnd(min, '0')

  // El cero negativo no existe para un humano: '-0,00 €' es una devolución de nada.
  const esCero = /^0*$/.test(ent) && /^0*$/.test(fra)
  return { neg: neg && !esCero, ent: ent || '0', fra }
}

function _agrupar(entero, separador) {
  if (!separador) return entero
  return entero.replace(/\B(?=(\d{3})+(?!\d))/g, separador)
}

/** El número ya escrito, o null si no era un número. */
function _texto(valor, cfg, { decimales, minimos, agrupar = true }) {
  const decimal = _aDecimal(valor)
  if (decimal === null) return null
  const { neg, ent, fra } = _redondear(decimal, decimales, minimos)
  const entero = agrupar === false ? ent : _agrupar(ent, cfg.separador_miles)
  return (neg ? '-' : '') + entero + (fra ? cfg.separador_decimal + fra : '')
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// LA BASE DE i18n — hoy no traduce nada, y esa es exactamente su gracia
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// El dueño ha dicho que "tarde o temprano hay que traducir el 100 % de la app". Son más de 8.000
// literales en castellano repartidos por las pantallas. El trabajo grande no es el motor: es SACAR
// esos literales, y eso solo se puede hacer poco a poco, pantalla a pantalla, sin que la app deje de
// funcionar ni un día. Para eso hace falta que exista YA una función que se pueda ir metiendo:
//
//     <h1>Configuración regional</h1>
//     <h1>{t('ajustes.regional.titulo', 'Configuración regional')}</h1>
//
// Las dos líneas pintan exactamente lo mismo mientras no haya diccionario. Se puede convertir media
// pantalla, subirla, y no cambia nada para nadie. Cuando existan los diccionarios, esas mismas
// llamadas empiezan a traducir solas.
//
// ── POR QUÉ NO react-i18next (todavía) ──────────────────────────────────────────────────────────
// Se ha valorado, y la decisión razonada está en el informe de la tanda. Resumida:
//   · La firma `t(clave, porDefecto)` de abajo es LA MISMA de i18next (`t(key, defaultValue)`), la
//     interpolación usa sus llaves dobles `{{nombre}}` y el plural sus sufijos `_one` / `_other`.
//     Es decir: los 8.000 sitios que se escriban con esto NO hay que volver a tocarlos el día que
//     entre la librería. Lo único que cambiaría es de dónde se importa `t`.
//   · Hoy la librería no haría nada: un solo idioma cargado y `t` devolviendo su segundo argumento.
//     Serían unas decenas de kB y un arranque asíncrono con Suspense a cambio de nada.
//   · El proyecto no tiene runner de tests en el frontend: `npm test` solo recorre el backend. Esta
//     función, al ser pura, se prueba hoy desde backend/services/formatos.test.js. Un motor de
//     librería, no.
//   · El día que haya un idioma con 4 o 6 formas de plural (ruso, polaco, árabe) o haya que cargar
//     diccionarios bajo demanda por ruta, ese día la librería se gana su sitio. Ese es el momento de
//     instalarla, no antes, y la migración será mecánica.
// La selección de plural, eso sí, NO se inventa: la hace `Intl.PluralRules`, que ya viene en el
// navegador y sabe las reglas de todos los idiomas. Cero bytes.

let _idioma = REGIONAL_POR_DEFECTO.idioma
let _dicc = null   // null = no hay traducciones cargadas: `t` devuelve siempre el texto por defecto

/**
 * Carga (o retira, con null) el diccionario del idioma activo. Formato plano, el de i18next:
 * `{ 'ajustes.regional.titulo': 'Regional settings', 'citas_one': '1 appointment', ... }`.
 * En R1 nadie llama a esto: el castellano vive en los propios `porDefecto`.
 */
export function cargarTraducciones(idioma, diccionario) {
  _idioma = idioma || REGIONAL_POR_DEFECTO.idioma
  _dicc = diccionario || null
}

export const idiomaActivo = () => _idioma

/** Qué forma de plural toca en ese idioma. Un idioma que Intl no conozca no puede tumbar un texto. */
function _formaPlural(idioma, count) {
  try { return new Intl.PluralRules(idioma || 'es').select(Number(count)) } catch { return 'other' }
}

/**
 * EL NÚCLEO PURO de `t`, separado para poder probarlo sin tocar el estado del módulo. Todo lo que
 * decide está aquí dentro.
 */
export function _traducir(dicc, idioma, clave, porDefecto, valores) {
  const v = valores || {}
  const conCuenta = v.count !== undefined && v.count !== null

  // Plural: con `count`, se pregunta a Intl qué forma toca en ESE idioma ('one', 'other', 'few'…) y
  // se busca 'clave_one'. Si el idioma no tiene esa forma o falta la entrada, se cae a 'other' y
  // luego a la clave pelada, que es como se comporta i18next.
  const candidatas = conCuenta
    ? [`${clave}_${_formaPlural(idioma, v.count)}`, `${clave}_other`, clave]
    : [clave]

  let texto
  for (const c of candidatas) {
    if (dicc && typeof dicc[c] === 'string') { texto = dicc[c]; break }
  }

  if (texto === undefined) {
    // Sin diccionario manda el texto por defecto, que es el castellano escrito en la pantalla.
    // Puede venir como objeto por formas de plural: { one: '1 cita', other: '{{count}} citas' }.
    if (porDefecto && typeof porDefecto === 'object') {
      const forma = _formaPlural(idioma, v.count)
      texto = porDefecto[forma] !== undefined ? porDefecto[forma] : porDefecto.other
    } else {
      texto = porDefecto
    }
  }

  // Sin texto por defecto se devuelve LA CLAVE, nunca 'undefined': una clave suelta en la pantalla
  // se ve fatal y se arregla en un minuto; un 'undefined' parece un fallo de la aplicación y nadie
  // sabe de dónde sale.
  if (texto === undefined || texto === null) return String(clave)
  texto = String(texto)

  // Interpolación `{{nombre}}`. Un hueco sin valor se deja TAL CUAL (se ve `{{nombre}}` y se
  // corrige) en vez de sustituirlo por 'undefined', que se lee como si el dato no existiera.
  if (texto.includes('{{')) {
    texto = texto.replace(/\{\{\s*(\w+)\s*\}\}/g, (todo, nombre) =>
      (v[nombre] === undefined || v[nombre] === null ? todo : String(v[nombre])))
  }
  return texto
}

/**
 * t('clave', 'Texto en castellano', { valores })
 *
 * HOY devuelve el texto por defecto. Devuelve texto PLANO: React lo escapa al pintarlo, así que no
 * se le pase nunca a dangerouslySetInnerHTML.
 */
export function t(clave, porDefecto, valores) {
  return _traducir(_dicc, _idioma, clave, porDefecto, valores)
}
